import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { DateField } from "@gigpro/ui/Form/DateField";
import { Select, SelectItem } from "@gigpro/ui/Form/Select";
import { Textarea } from "@gigpro/ui/Form/Textarea";
import { TimeField } from "@gigpro/ui/Form/TimeField";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { Delete, Plus } from "@gigpro/ui/Icons";
import { Label } from "@gigpro/ui/Label";
import { Separator } from "@gigpro/ui/Separator";
import { useNow } from "@gigpro/ui/utils/useNow";
import { shortenTimezone } from "@gigpro/utils/datetime";
import { ApiError } from "@sidegigllc/api";
import {
  type AdminGigApplication,
  timesheetsShiftBreaksCreateWithTransform,
  timesheetsShiftBreaksDestroy,
  timesheetsShiftBreaksPartialUpdateWithTransform,
} from "@sidegigllc/api/client";
import { useMutation } from "@tanstack/react-query";
import { type ShiftBreak, shiftBreakFieldsSchema } from "api/resources/shared";
import { formatDistanceStrict, isSameMinute } from "date-fns";
import { ApplicationSummary } from "domains/admin/components/ApplicationSummary";
import { BreakChangeHistory } from "domains/admin/containers/BreakChangeHistory";
import { humanize } from "domains/admin/humanize";
import React from "react";
import { useFieldArray, useFormState, useWatch } from "react-hook-form";
import type { ModalBaseProps } from "types/utils";
import * as z from "zod/mini";

type EditBreaksDialogProps = ModalBaseProps<"new"> & {
  application: AdminGigApplication;
  refetchApplication: () => void;
  onSuccess: () => void;
};

export function EditBreaksDialog(props: Readonly<EditBreaksDialogProps>) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogTitle>Edit Breaks</DialogTitle>
      <DialogBody {...props} />
    </Dialog>
  );
}

const EXPLANATION_MIN_LENGTH = 30;

// The API types `skipped_break_reason` as a bare string, so the option list lives on the FE.
const skippedBreakReasons = [
  "UNDERSTAFFED",
  "HIGH_DEMAND",
  "PERSONAL_CHOICE",
  "NOT_OFFERED",
  "LATE_BREAK",
  "EDIT_BREAK_TIME",
  "FORGOT_TO_TRACK",
  "OTHER",
] as const;

const breakReasonBaseline = (data: {
  skipped_break_reason?: string | null;
  skipped_break_explanation?: string | null;
}) => ({
  reason: data.skipped_break_reason?.trim() ?? "",
  explanation: data.skipped_break_explanation?.trim() ?? "",
});
type BreakReasonBaseline = ReturnType<typeof breakReasonBaseline>;

const makeFormSchema = (baselines: Map<string, BreakReasonBaseline>) => {
  const breakFieldsSchema = z
    .required(z.partial(shiftBreakFieldsSchema, { id: true, compliance_status: true }), {
      start_time: true,
      end_time: true,
    })
    .check(
      z.superRefine((data, ctx) => {
        const reason = data.skipped_break_reason?.trim() ?? "";
        const explanation = data.skipped_break_explanation?.trim() ?? "";
        const baseline = data.id ? baselines.get(data.id) : undefined;
        const matchesStored = reason === baseline?.reason && explanation === baseline?.explanation;
        // Enforce the minimum only on a reason/explanation the admin actually sets, so a shorter
        // value stored before this rule existed does not wedge an otherwise unrelated edit.
        if (reason && explanation.length < EXPLANATION_MIN_LENGTH && !matchesStored) {
          ctx.addIssue({
            code: "custom",
            path: ["skipped_break_explanation"],
            message: `Enter at least ${EXPLANATION_MIN_LENGTH} characters.`,
          });
        }
        if (explanation && !reason && !matchesStored) {
          ctx.addIssue({ code: "custom", path: ["skipped_break_reason"], message: "Select a reason." });
        }
      }),
    );
  return z.object({ breaks: z.array(breakFieldsSchema) });
};
type FormValues = z.infer<ReturnType<typeof makeFormSchema>>;

function DialogBody({ onSuccess, application, refetchApplication }: Readonly<EditBreaksDialogProps>) {
  const timezone = shortenTimezone(application.timezone);
  const breaks = application.timesheet?.shift_breaks;
  const existingBreaks = (breaks ?? []).map(
    ({ id, start_time, end_time, minutes, seconds, skipped_break_reason, skipped_break_explanation }) => ({
      id,
      start_time,
      end_time,
      minutes,
      seconds,
      skipped_break_reason,
      skipped_break_explanation,
    }),
  );
  const reasonBaselines = new Map((breaks ?? []).map((b) => [b.id, breakReasonBaseline(b)]));
  const formSchema = makeFormSchema(reasonBaselines);
  const form = useZodForm({ schema: formSchema, defaultValues: { breaks: existingBreaks } });

  React.useEffect(() => {
    const newBreaks = form.getValues("breaks").filter((b) => !b.id);
    form.reset({ breaks: [...existingBreaks, ...newBreaks] });
  }, [existingBreaks, form]);

  const deleteBreak = useMutation({
    mutationFn: (variables: { id: string; index: number }) => timesheetsShiftBreaksDestroy(variables.id),
    onSuccess: refetchApplication,
    onError: (error, variables) => {
      const apiError = ApiError.fromThrown<ShiftBreak>(error);
      form.setError(`breaks.${variables.index}`, { type: "api", message: apiError.getMainErrorMessage() });
    },
  });

  const mappedBreaks = new Map(breaks?.map((b) => [b.id, b]));
  const onSubmit = async ({ breaks: data }: FormValues) => {
    let hasErrors = false;
    for (let index = 0; index < data.length; index++) {
      const breakData = data[index];
      try {
        if (!breakData.start_time || !breakData.end_time) continue;
        if (!breakData.id) {
          await timesheetsShiftBreaksCreateWithTransform({
            application_id: application.id,
            start_time: breakData.start_time,
            end_time: breakData.end_time,
            timezone: application.timezone,
            skipped_break_reason: breakData.skipped_break_reason ?? undefined,
            skipped_break_explanation: breakData.skipped_break_explanation ?? undefined,
          });
          continue;
        }
        const ogBreak = mappedBreaks.get(breakData.id);
        const timesUnchanged =
          ogBreak?.start_time != null &&
          ogBreak.end_time != null &&
          isSameMinute(ogBreak.start_time, breakData.start_time) &&
          isSameMinute(ogBreak.end_time, breakData.end_time);
        const ogBaseline = ogBreak ? breakReasonBaseline(ogBreak) : undefined;
        const editBaseline = breakReasonBaseline(breakData);
        const reasonUnchanged =
          editBaseline.reason === ogBaseline?.reason && editBaseline.explanation === ogBaseline?.explanation;
        if (!ogBreak || (timesUnchanged && reasonUnchanged)) {
          // Nothing to send: start/end time and reason/explanation all match the stored break.
          continue;
        }
        await timesheetsShiftBreaksPartialUpdateWithTransform({
          id: breakData.id,
          data: {
            start_time: breakData.start_time,
            end_time: breakData.end_time,
            timezone: application.timezone,
            skipped_break_reason: breakData.skipped_break_reason ?? undefined,
            skipped_break_explanation: breakData.skipped_break_explanation ?? undefined,
          },
        });
      } catch (error) {
        hasErrors = true;
        const apiError = ApiError.fromThrown<ShiftBreak>(error);
        const fieldPaths = {
          start_time: `breaks.${index}.start_time`,
          end_time: `breaks.${index}.end_time`,
          compliance_status: `breaks.${index}.compliance_status`,
          skipped_break_reason: `breaks.${index}.skipped_break_reason`,
          skipped_break_explanation: `breaks.${index}.skipped_break_explanation`,
        } as const;
        const hasFieldError = (Object.keys(fieldPaths) as Array<keyof typeof fieldPaths>).some((field) =>
          apiError.hasFieldError(field),
        );
        if (hasFieldError) {
          apiError.setFormErrors(form.setError, fieldPaths);
        } else {
          form.setError(`breaks.${index}`, { type: "api", message: apiError.getMainErrorMessage() });
        }
      }
    }
    if (!hasErrors) onSuccess();
  };

  const isLoading = form.formState.isSubmitting || deleteBreak.isPending;
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "breaks" });
  const watchedBreaks = useWatch({ control: form.control, name: "breaks" });
  const formState = useFormState({ control: form.control });
  const now = useNow();

  return (
    <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-3">
      <ApplicationSummary application={application} />
      <Button variant="outlined" onClick={() => append({} as FormValues["breaks"][number])} disabled={isLoading}>
        <Plus /> Add Break
      </Button>
      {fields.map(({ id }, index) => {
        const breakId = form.getValues(`breaks.${index}.id`);
        return (
          <div key={id} className="grid grid-cols-2 items-center gap-2">
            {index !== 0 && <Separator className="col-span-2" />}
            <div className="col-span-2 flex">
              <Label htmlFor={id} className="text-large font-semibold">
                Break {index + 1} {!breakId && "(New)"}
              </Label>
              <Button
                icon
                onClick={() => {
                  if (breakId) deleteBreak.mutate({ id: breakId, index });
                  else remove(index);
                }}
                loading={deleteBreak.isPending && deleteBreak.variables?.index === index}
                disabled={isLoading}
              >
                <Delete />
              </Button>
            </div>
            <DateField name={`breaks.${index}.start_time`} control={form.control} label="Start Date" />
            <TimeField
              name={`breaks.${index}.start_time`}
              control={form.control}
              label="Start Time"
              timezone={timezone}
            />
            <DateField name={`breaks.${index}.end_time`} control={form.control} label="End Date" />
            <TimeField name={`breaks.${index}.end_time`} control={form.control} label="End Time" timezone={timezone} />
            <p>
              <b>Duration:</b>{" "}
              {formatDistanceStrict(watchedBreaks[index]?.start_time ?? now, watchedBreaks[index]?.end_time ?? now)}
            </p>
            <p className="col-span-2 font-semibold text-wrap text-error">
              {form.getFieldState(`breaks.${index}`, formState).error?.message ??
                form.getFieldState(`breaks.${index}.compliance_status`, formState).error?.message}
            </p>
            <Select
              control={form.control}
              name={`breaks.${index}.skipped_break_reason`}
              label="Reason"
              placeholder="Select a reason"
              className="col-span-2"
            >
              {skippedBreakReasons.map((reason) => (
                <SelectItem key={reason} value={reason}>
                  {humanize(reason)}
                </SelectItem>
              ))}
            </Select>
            <Textarea
              control={form.control}
              name={`breaks.${index}.skipped_break_explanation`}
              label="Explanation"
              placeholder={`Explain the reason (min ${EXPLANATION_MIN_LENGTH} characters)`}
              className="col-span-2"
            />
            {breakId && <BreakChangeHistory breakId={breakId} className="col-span-2" />}
          </div>
        );
      })}
      <DialogFooter>
        <Button onClick={onSuccess} className="w-full" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="w-full"
          variant="contained"
          disabled={isLoading}
          loading={form.formState.isSubmitting}
        >
          Save
        </Button>
      </DialogFooter>
    </Form>
  );
}
